function ensureTextEncoder() {
  if (typeof TextEncoder === "undefined") {
    throw new Error("TextEncoder is not available in the current runtime");
  }
  return new TextEncoder();
}

function toBodyString(body) {
  if (body == null) {
    return "";
  }
  if (typeof body === "string") {
    return body;
  }
  return JSON.stringify(body);
}

function normalizePath(pathOrUrl, baseURL) {
  if (!pathOrUrl) {
    throw new Error("path is required");
  }

  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return new URL(pathOrUrl).pathname;
  }

  if (pathOrUrl.startsWith("/")) {
    return pathOrUrl;
  }

  if (baseURL) {
    return new URL(pathOrUrl, baseURL).pathname;
  }

  return pathOrUrl;
}

function buildURL(baseURL, path) {
  if (!baseURL) {
    return path;
  }
  return new URL(path, baseURL).toString();
}

function randomHex(bytes) {
  const buffer = new Uint8Array(bytes);

  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(buffer);
  } else {
    for (let i = 0; i < buffer.length; i += 1) {
      buffer[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(buffer, (value) => value.toString(16).padStart(2, "0")).join("");
}

function bindSocketEvent(socket, eventName, handler) {
  if (typeof socket.addEventListener === "function") {
    socket.addEventListener(eventName, handler);
    return;
  }

  if (typeof socket.on === "function") {
    socket.on(eventName, handler);
    return;
  }

  throw new Error(`WebSocket implementation does not support ${eventName} events`);
}

export function createSM3Adapter(sm3Impl) {
  if (!sm3Impl || typeof sm3Impl !== "function" || typeof sm3Impl.hmac !== "function") {
    throw new Error("A valid sm3 implementation with sm3() and sm3.hmac() is required");
  }

  const encoder = ensureTextEncoder();

  return {
    hashHex(text) {
      return sm3Impl(encoder.encode(String(text)));
    },
    hmacHex(message, secret) {
      return sm3Impl.hmac(encoder.encode(String(secret)), encoder.encode(String(message)));
    },
  };
}

export class AuthKeyIDPClient {
  constructor({
    appId,
    appSecret,
    baseURL = "",
    wsBaseURL = "",
    fetchImpl = globalThis.fetch?.bind(globalThis),
    WebSocketImpl = globalThis.WebSocket,
    cryptoAdapter,
  }) {
    if (!appId) {
      throw new Error("appId is required");
    }
    if (!appSecret) {
      throw new Error("appSecret is required");
    }
    if (!cryptoAdapter || typeof cryptoAdapter.hashHex !== "function" || typeof cryptoAdapter.hmacHex !== "function") {
      throw new Error("cryptoAdapter with hashHex() and hmacHex() is required");
    }

    this.appId = appId;
    this.appSecret = appSecret;
    this.baseURL = baseURL;
    this.wsBaseURL = wsBaseURL;
    this.fetchImpl = fetchImpl;
    this.WebSocketImpl = WebSocketImpl;
    this.crypto = cryptoAdapter;
  }

  createNonce() {
    if (globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID().replace(/-/g, "");
    }
    return randomHex(16);
  }

  createTimestamp() {
    return String(Math.floor(Date.now() / 1000));
  }

  hashBody(body) {
    return this.crypto.hashHex(toBodyString(body));
  }

  buildCanonicalString({ method, path, timestamp, nonce, body = "" }) {
    return [
      String(method || "GET").toUpperCase(),
      normalizePath(path, this.baseURL),
      timestamp,
      nonce,
      this.hashBody(body),
    ].join("\n");
  }

  sign({ method, path, body = "", timestamp = this.createTimestamp(), nonce = this.createNonce() }) {
    const canonical = this.buildCanonicalString({ method, path, timestamp, nonce, body });
    const signature = this.crypto.hmacHex(canonical, this.appSecret);

    return {
      appId: this.appId,
      timestamp,
      nonce,
      signature,
      canonical,
      bodyHash: this.hashBody(body),
    };
  }

  buildHeaders({ method, path, body = "", headers = {} }) {
    const signed = this.sign({ method, path, body });
    return {
      ...headers,
      "X-App-Id": this.appId,
      "X-Timestamp": signed.timestamp,
      "X-Nonce": signed.nonce,
      "X-Signature": signed.signature,
    };
  }

  async request(path, { method = "GET", body, headers = {}, parseAs = "json" } = {}) {
    if (!this.fetchImpl) {
      throw new Error("fetch is not available; please provide fetchImpl");
    }

    const upperMethod = method.toUpperCase();
    const bodyString = upperMethod === "GET" ? "" : toBodyString(body);
    const finalHeaders = this.buildHeaders({
      method: upperMethod,
      path,
      body: bodyString,
      headers: bodyString ? { "Content-Type": "application/json", ...headers } : headers,
    });

    const response = await this.fetchImpl(buildURL(this.baseURL, path), {
      method: upperMethod,
      headers: finalHeaders,
      body: upperMethod === "GET" ? undefined : bodyString,
    });

    if (parseAs === "raw") {
      return response;
    }

    const payload = parseAs === "text" ? await response.text() : await response.json();
    if (!response.ok) {
      const error = new Error(`Request failed with status ${response.status}`);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }
    return payload;
  }

  getQRCode(payload) {
    return this.request("/api/v1/getqr", {
      method: "POST",
      body: payload,
    });
  }

  saveTransformToken(payload) {
    return this.request("/api/v1/saveTransformToken", {
      method: "POST",
      body: payload,
    });
  }

  getChallenge(payload) {
    return this.request("/api/v1/challenge", {
      method: "POST",
      body: payload,
    });
  }

  transformToken(payload) {
    return this.request("/api/v1/authtoken", {
      method: "POST",
      body: payload,
    });
  }

  verifyPlainToken(payload) {
    return this.request("/api/v1/verifyplaintoken", {
      method: "POST",
      body: payload,
    });
  }

  buildWebSocketURL({
    path = "/qrResult",
    wsBaseURL = this.wsBaseURL || this.baseURL,
    timestamp = this.createTimestamp(),
    nonce = this.createNonce(),
  } = {}) {
    const signed = this.sign({
      method: "GET",
      path,
      body: "",
      timestamp,
      nonce,
    });

    const wsURL = new URL(buildURL(wsBaseURL, path));
    if (wsURL.protocol === "http:") {
      wsURL.protocol = "ws:";
    }
    if (wsURL.protocol === "https:") {
      wsURL.protocol = "wss:";
    }

    wsURL.searchParams.set("app_id", this.appId);
    wsURL.searchParams.set("ts", signed.timestamp);
    wsURL.searchParams.set("nonce", signed.nonce);
    wsURL.searchParams.set("sig", signed.signature);
    return wsURL.toString();
  }

  connectQRResult({
    ssid,
    path = "/qrResult",
    wsBaseURL,
    onOpen,
    onMessage,
    onError,
    onClose,
  }) {
    if (!ssid) {
      throw new Error("ssid is required");
    }
    if (!this.WebSocketImpl) {
      throw new Error("WebSocket is not available; please provide WebSocketImpl");
    }

    const ws = new this.WebSocketImpl(this.buildWebSocketURL({ path, wsBaseURL }));

    bindSocketEvent(ws, "open", () => {
      ws.send(JSON.stringify({ ssid }));
      if (onOpen) {
        onOpen(ws);
      }
    });

    bindSocketEvent(ws, "message", (eventOrData) => {
      const rawData =
        eventOrData && typeof eventOrData === "object" && "data" in eventOrData
          ? eventOrData.data
          : eventOrData;

      const textData =
        typeof rawData === "string"
          ? rawData
          : rawData instanceof Uint8Array || rawData instanceof ArrayBuffer
            ? new TextDecoder().decode(rawData)
            : String(rawData);

      let parsed = textData;
      try {
        parsed = JSON.parse(textData);
        if (parsed?.status === "success" && typeof parsed.data === "string") {
          parsed.data = JSON.parse(parsed.data);
        }
      } catch (error) {
        // Keep the original payload when the frame is not JSON.
      }

      if (onMessage) {
        onMessage(parsed, eventOrData, ws);
      }
    });

    if (onError) {
      bindSocketEvent(ws, "error", (event) => onError(event, ws));
    }

    if (onClose) {
      bindSocketEvent(ws, "close", (event) => onClose(event, ws));
    }

    return ws;
  }
}
